import React, { useState, useEffect } from 'react'
import {
  Box,
  Flex,
  Text,
  Input,
  Button,
  Switch,
  FormControl,
  FormLabel,
  FormHelperText,
  Badge,
  HStack,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  useToast,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import Card from 'components/card/Card'
import { AddIcon, RepeatIcon, CheckIcon } from '@chakra-ui/icons'
import {
  useGetCarouselHeadline,
  useUpdateCarouselHeadline,
} from 'queries/carousel.query'

const PRESET_OPTION_2 = {
  is_active: true,
  en: {
    prefix: 'Celebrate Unforgettable',
    words: ['Concerts', 'Live Shows', 'Memories', 'Evenings'],
  },
  de: {
    prefix: 'Erlebe unvergessliche',
    words: ['Konzerte', 'Live-Shows', 'Erinnerungen', 'Abende'],
  },
  fa: {
    prefix: 'تجربه شورانگیزِ',
    words: ['کنسرت‌های زنده', 'اجراهای ویژه', 'خاطرات ماندگار', 'شب‌های به یادماندنی'],
  },
}

export default function CarouselHeadlineManager() {
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const textColorSec = useColorModeValue('gray.500', 'gray.400')
  const bgCard = useColorModeValue('white', 'navy.800')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200')
  const previewBg = useColorModeValue('gray.900', '#0B0F14')
  const inputBg = useColorModeValue('gray.50', 'navy.900')
  const toast = useToast()

  const { data: headlineData } = useGetCarouselHeadline()
  const updateMutation = useUpdateCarouselHeadline()

  const [isActive, setIsActive] = useState(true)
  const [activeLang, setActiveLang] = useState('en')
  const [newWordInput, setNewWordInput] = useState('')

  const [languages, setLanguages] = useState({
    en: { prefix: '', words: [] },
    de: { prefix: '', words: [] },
    fa: { prefix: '', words: [] },
  })

  // Synchronize state when data is loaded from API
  useEffect(() => {
    if (headlineData) {
      setIsActive(headlineData.is_active ?? true)
      setLanguages({
        en: {
          prefix: headlineData.en?.prefix ?? PRESET_OPTION_2.en.prefix,
          words: headlineData.en?.words ?? PRESET_OPTION_2.en.words,
        },
        de: {
          prefix: headlineData.de?.prefix ?? PRESET_OPTION_2.de.prefix,
          words: headlineData.de?.words ?? PRESET_OPTION_2.de.words,
        },
        fa: {
          prefix: headlineData.fa?.prefix ?? PRESET_OPTION_2.fa.prefix,
          words: headlineData.fa?.words ?? PRESET_OPTION_2.fa.words,
        },
      })
    }
  }, [headlineData])

  // Live preview typewriter simulation
  const currentLangData = languages[activeLang] || languages.en
  const [previewWordIndex, setPreviewWordIndex] = useState(0)
  const [displayedText, setDisplayedText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    const currentWords = currentLangData.words || []
    if (currentWords.length === 0) {
      setDisplayedText('')
      return
    }

    const word = currentWords[previewWordIndex % currentWords.length] || ''
    const typingSpeed = isDeleting ? 40 : 80

    const timer = setTimeout(() => {
      if (!isDeleting) {
        if (displayedText.length < word.length) {
          setDisplayedText(word.slice(0, displayedText.length + 1))
        } else {
          // Pause before deleting
          setTimeout(() => setIsDeleting(true), 1600)
        }
      } else {
        if (displayedText.length > 0) {
          setDisplayedText(word.slice(0, displayedText.length - 1))
        } else {
          setIsDeleting(false)
          setPreviewWordIndex((prev) => (prev + 1) % currentWords.length)
        }
      }
    }, typingSpeed)

    return () => clearTimeout(timer)
  }, [displayedText, isDeleting, previewWordIndex, currentLangData.words])

  const handlePrefixChange = (lang, value) => {
    setLanguages((prev) => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        prefix: value,
      },
    }))
  }

  const handleAddWord = (lang) => {
    const trimmed = newWordInput.trim()
    if (!trimmed) return

    setLanguages((prev) => {
      const currentList = prev[lang]?.words || []
      if (currentList.includes(trimmed)) return prev
      return {
        ...prev,
        [lang]: {
          ...prev[lang],
          words: [...currentList, trimmed],
        },
      }
    })
    setNewWordInput('')
  }

  const handleRemoveWord = (lang, wordToRemove) => {
    setLanguages((prev) => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        words: prev[lang].words.filter((w) => w !== wordToRemove),
      },
    }))
  }

  const handleResetToOption2 = () => {
    setLanguages({
      en: { ...PRESET_OPTION_2.en },
      de: { ...PRESET_OPTION_2.de },
      fa: { ...PRESET_OPTION_2.fa },
    })
    setIsActive(true)
    toast({
      title: 'Default Preset Loaded',
      description: 'Option 2 headline with English, German and Dari translations loaded. Click Save Changes to apply.',
      status: 'info',
      duration: 3500,
      isClosable: true,
    })
  }

  const handleSave = () => {
    updateMutation.mutate(
      {
        is_active: isActive,
        en: languages.en,
        de: languages.de,
        fa: languages.fa,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Settings Saved',
            description: 'Carousel headline settings for all 3 languages updated successfully.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
        },
        onError: (err) => {
          toast({
            title: 'Error Saving Settings',
            description: err?.message || 'An error occurred while saving.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          })
        },
      }
    )
  }

  const langConfig = [
    { code: 'en', name: 'English', dir: 'ltr', placeholder: 'e.g. Celebrate Unforgettable' },
    { code: 'de', name: 'Deutsch (German)', dir: 'ltr', placeholder: 'e.g. Erlebe unvergessliche' },
    { code: 'fa', name: 'Dari / Persian (دری)', dir: 'rtl', placeholder: 'مثال: تجربه شورانگیزِ' },
  ]

  return (
    <Card p='24px' mb='24px' bg={bgCard} border='1px solid' borderColor={borderColor} shadow='sm'>
      {/* Header */}
      <Flex direction={{ base: 'column', md: 'row' }} justify='space-between' align={{ md: 'center' }} mb='20px' gap='4'>
        <Box>
          <HStack spacing='3'>
            <Text fontSize='xl' fontWeight='bold' color={textColor}>
              Carousel Animated Headline
            </Text>
            <Badge colorScheme={isActive ? 'green' : 'gray'} px='2' py='0.5' borderRadius='full'>
              {isActive ? 'Active' : 'Disabled'}
            </Badge>
          </HStack>
          <Text fontSize='sm' color={textColorSec} mt='1'>
            Configure static prefix and rotating keywords with typewriter motion in 3 languages (English, German, Dari).
          </Text>
        </Box>

        <HStack spacing='4'>
          <FormControl display='flex' alignItems='center' width='auto'>
            <FormLabel htmlFor='headline-active-switch' mb='0' fontSize='sm' fontWeight='medium' color={textColor}>
              Visible on Site:
            </FormLabel>
            <Switch
              id='headline-active-switch'
              colorScheme='brandScheme'
              isChecked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
          </FormControl>

          <Button
            size='sm'
            variant='outline'
            leftIcon={<RepeatIcon />}
            onClick={handleResetToOption2}
            title='Reset to Option 2 default'
          >
            Reset to Default
          </Button>

          <Button
            size='sm'
            colorScheme='brandScheme'
            leftIcon={<CheckIcon />}
            isLoading={updateMutation.isPending}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </HStack>
      </Flex>

      {/* Live Preview Box */}
      <Box
        bg={previewBg}
        borderRadius='16px'
        p={{ base: '16px', md: '24px' }}
        mb='24px'
        border='1px solid'
        borderColor='whiteAlpha.200'
        textAlign='center'
        position='relative'
        overflow='hidden'
      >
        <Badge
          position='absolute'
          top='3'
          right='3'
          colorScheme='yellow'
          fontSize='xs'
          px='2'
          borderRadius='md'
        >
          Live Preview ({langConfig.find((l) => l.code === activeLang)?.name})
        </Badge>

        <VStack spacing='2' py='4'>
          <Text fontSize='xs' textTransform='uppercase' letterSpacing='widest' color='gray.400'>
            Bamyan Entertainment Hero Headline
          </Text>

          {isActive ? (
            <Flex
              direction='row'
              wrap='nowrap'
              align='center'
              justify='center'
              gap='2'
              whiteSpace='nowrap'
              overflow='hidden'
              maxW='100%'
              fontSize={{ base: 'md', sm: 'lg', md: '2xl', lg: '3xl' }}
              fontWeight='900'
              dir={currentLangData === languages.fa ? 'rtl' : 'ltr'}
            >
              <Text as='span' color='white' textShadow='0 2px 10px rgba(0,0,0,0.5)'>
                {currentLangData.prefix || '—'}
              </Text>
              <Text
                as='span'
                color='#E0A82E'
                bgGradient='linear(to-r, #E0A82E, #F59E0B)'
                bgClip='text'
                textShadow='0 2px 12px rgba(224, 168, 46, 0.4)'
                borderBottom='2px solid'
                borderColor='#E0A82E'
                pb='1px'
                display='inline-flex'
                alignItems='center'
              >
                {displayedText || '...'}
                <Box
                  as='span'
                  w='2px'
                  h='1em'
                  bg='#E0A82E'
                  ml='4px'
                  display='inline-block'
                  animation='blink 1s step-start infinite'
                />
              </Text>
            </Flex>
          ) : (
            <Text color='gray.500' fontStyle='italic' fontSize='sm'>
              Headline is currently disabled and will not show on the website.
            </Text>
          )}
        </VStack>
      </Box>

      {/* Multilingual Tabs */}
      <Tabs
        variant='enclosed'
        colorScheme='brandScheme'
        index={langConfig.findIndex((l) => l.code === activeLang)}
        onChange={(idx) => {
          setActiveLang(langConfig[idx].code)
          setNewWordInput('')
        }}
      >
        <TabList borderColor={borderColor}>
          {langConfig.map((item) => (
            <Tab key={item.code} fontWeight='bold' fontSize='sm'>
              {item.name}
            </Tab>
          ))}
        </TabList>

        <TabPanels pt='20px'>
          {langConfig.map((item) => {
            const langData = languages[item.code] || { prefix: '', words: [] }
            return (
              <TabPanel key={item.code} p='0'>
                <VStack spacing='20px' align='stretch'>
                  {/* Prefix Input */}
                  <FormControl>
                    <FormLabel fontSize='sm' fontWeight='bold' color={textColor}>
                      Static Prefix ({item.name})
                    </FormLabel>
                    <Input
                      dir={item.dir}
                      value={langData.prefix}
                      onChange={(e) => handlePrefixChange(item.code, e.target.value)}
                      placeholder={item.placeholder}
                      bg={inputBg}
                      borderColor={borderColor}
                      fontSize='md'
                      fontWeight='medium'
                    />
                    <FormHelperText color={textColorSec}>
                      The static text that appears right before the rotating typewriter words.
                    </FormHelperText>
                  </FormControl>

                  {/* Dynamic Words Input & Tags */}
                  <FormControl>
                    <FormLabel fontSize='sm' fontWeight='bold' color={textColor}>
                      Rotating Dynamic Keywords ({item.name})
                    </FormLabel>

                    {/* Word tags */}
                    <Wrap spacing='2' mb='12px'>
                      {(langData.words || []).map((word, wIdx) => (
                        <WrapItem key={wIdx}>
                          <Tag
                            size='lg'
                            borderRadius='full'
                            variant='subtle'
                            colorScheme='yellow'
                            px='3'
                            py='1'
                          >
                            <TagLabel fontWeight='bold'>{word}</TagLabel>
                            <TagCloseButton
                              onClick={() => handleRemoveWord(item.code, word)}
                            />
                          </Tag>
                        </WrapItem>
                      ))}
                      {(langData.words || []).length === 0 && (
                        <Text fontSize='sm' color='gray.400' fontStyle='italic'>
                          No keywords added yet. Type a keyword below and press Enter or click Add.
                        </Text>
                      )}
                    </Wrap>

                    {/* Add new word input */}
                    <HStack>
                      <Input
                        dir={item.dir}
                        value={newWordInput}
                        onChange={(e) => setNewWordInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            handleAddWord(item.code)
                          }
                        }}
                        placeholder={`Type a new keyword for ${item.name} and press Enter...`}
                        bg={inputBg}
                        borderColor={borderColor}
                      />
                      <Button
                        leftIcon={<AddIcon />}
                        colorScheme='brandScheme'
                        onClick={() => handleAddWord(item.code)}
                        px='6'
                      >
                        Add
                      </Button>
                    </HStack>
                    <FormHelperText color={textColorSec}>
                      You can add multiple keywords. They will rotate one after another with the typewriter effect.
                    </FormHelperText>
                  </FormControl>
                </VStack>
              </TabPanel>
            )
          })}
        </TabPanels>
      </Tabs>
    </Card>
  )
}
